/**
 * EmailSidebar — folder list, mailbox avatar picker, tag list.
 * Data comes from store.mailboxes/emailFolders/emailTags signals (bootstrapped on init).
 * Live unread counts come from store.sidebarCounts (ActionCable EmailJsonChannel).
 *
 * Layout:
 *   Left rail: regular mailbox avatars + personal mailbox avatars (avatar switching)
 *   Right panel:
 *     1. Compose button
 *     2. Active mailbox/inbox label
 *     3. System folders
 *     4. Custom folders
 *     5. Tags
 *     6. Settings (collapsible)
 */

import { h, Fragment } from 'preact'
import { useState } from 'preact/hooks'
import { post } from '@rails/request.js'
import { resolveFolderLabel } from '@tshmanager/email-shared'
import { emails_folders_path } from '../../routes.js'
import { MailboxAvatar } from './MailboxAvatar.jsx'

const SYSTEM_FOLDERS = [
  { key: 'inbox',    icon: 'bi-inbox',         labelKey: 'emails.folders.inbox' },
  { key: 'starred',  icon: 'bi-star',           labelKey: 'emails.folders.starred' },
  { key: 'sent',     icon: 'bi-send',           labelKey: 'emails.folders.sent' },
  { key: 'drafts',   icon: 'bi-file-earmark',   labelKey: 'emails.folders.drafts' },
  { key: 'archived', icon: 'bi-archive',        labelKey: 'emails.folders.archived' },
  { key: 'trash',    icon: 'bi-trash',          labelKey: 'emails.folders.trash' },
]


export function EmailSidebar({ store, i18n }) {
  const activeFolder      = store.activeFolder.value
  const activeMailbox     = store.activeMailbox.value
  const mailboxes         = store.mailboxes.value
  const personalMailboxes = store.personalMailboxes.value
  const emailFolders      = store.emailFolders.value
  const emailTags         = store.emailTags.value
  const counts            = store.sidebarCounts.value
  const isMobile          = store.isMobileLayout.value

  const [settingsOpen, setSettingsOpen] = useState(false)

  function getFolderCount(key) {
    return counts?.system_folders?.[key] || 0
  }

  function getCustomFolderCount(id) {
    return counts?.custom_folders?.[id] || 0
  }

  function getTagCount(id) {
    return counts?.tags?.[id] || 0
  }

  function handleFolderClick(e, folderKey) {
    e.preventDefault()
    store.setActiveFolder(folderKey)
  }

  function handleMailboxClick(e, mailboxId) {
    e.preventDefault()
    store.setActiveMailbox(mailboxId)
  }

  function handlePersonalMailboxClick(e, pm) {
    e.preventDefault()
    store.setActiveFolder(`personal_${pm.id}`)
  }

  async function handleNewFolderSubmit(e) {
    e.preventDefault()
    const name = e.target.elements['email_folder[name]']?.value?.trim()
    if (!name) return
    const response = await post(emails_folders_path({ format: 'json' }), {
      body: JSON.stringify({ email_folder: { name } }),
      responseKind: 'json',
    })
    if (response.ok) {
      const data = await response.json
      if (data.id) {
        store.emailFolders.value = [...store.emailFolders.value, data]
        e.target.reset()
        e.target.closest('[data-new-folder-form]')?.classList.add('d-none')
      }
    }
  }

  // Determine active mailbox/inbox label shown under compose button
  const activePersonalMailbox = personalMailboxes.find(
    pm => activeFolder === `personal_${pm.id}`
  )
  const activeMailboxName = activePersonalMailbox
    ? activePersonalMailbox.email
    : activeMailbox
      ? (mailboxes.find(mb => mb.id === activeMailbox)?.name || '')
      : i18n?.t('emails.all_mailboxes')

  // Personal mailboxes to show in avatar rail — filter to active mailbox when one is selected
  const visiblePersonalMailboxes = activeMailbox === null
    ? personalMailboxes
    : personalMailboxes.filter(pm => pm.mailbox_id === activeMailbox)

  const hasRail = mailboxes.length > 0 || visiblePersonalMailboxes.length > 0

  // Active folder display name (for mobile header "open list" button)
  const activeFolderDef = SYSTEM_FOLDERS.find(f => f.key === activeFolder)
  const activeFolderLabel = activeFolderDef
    ? i18n?.t(activeFolderDef.labelKey)
    : resolveFolderLabel(activeFolder, { emailFolders, emailTags })
      ?? (activeFolder.startsWith('personal_')
        ? personalMailboxes.find(pm => pm.id === Number(activeFolder.replace('personal_', '')))?.email
        : activeFolder)

  return (
    <div class="email-sidebar-inner">

      {/* Mobile header: app title + open list button */}
      {isMobile && (
        <div class="email-sidebar-mobile-header d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <span class="fw-semibold" style="font-size:1rem">
            <i class="bi bi-envelope me-2 text-primary" />
            {i18n?.t('emails.app_title') || 'Levelek'}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => store.showListPanel()}
          >
            <span style="font-size:0.8rem">{activeFolderLabel}</span>
            <i class="bi bi-chevron-right" style="font-size:0.75rem" />
          </button>
        </div>
      )}

      {/* Left: vertical mailbox avatar rail */}
      {hasRail && (
        <div class="email-mailbox-rail">
          {/* "All mailboxes" button */}
          {mailboxes.length > 0 && (
            <button
              type="button"
              class={`mailbox-avatar-btn ${activeMailbox === null && !activePersonalMailbox ? 'active' : ''}`}
              title={i18n?.t('emails.all_mailboxes')}
              onClick={e => handleMailboxClick(e, null)}
            >
              <i class="bi bi-envelope-open" style="font-size:0.9rem;color:#6c757d" />
            </button>
          )}

          {/* Regular mailbox avatars */}
          {mailboxes.map(mb => (
            <button
              key={mb.id}
              type="button"
              class={`mailbox-avatar-btn ${activeMailbox === mb.id && !activePersonalMailbox ? 'active' : ''}`}
              title={mb.name}
              onClick={e => handleMailboxClick(e, mb.id)}
            >
              <MailboxAvatar mailbox={mb} />
            </button>
          ))}

          {/* Separator + personal mailbox avatars */}
          {visiblePersonalMailboxes.length > 0 && (
            <>
              <div class="mailbox-rail-divider" />
              {visiblePersonalMailboxes.map(pm => {
                const initials = (pm.email || '?').split('@')[0].slice(0, 2).toUpperCase()
                const isActive = activeFolder === `personal_${pm.id}`
                return (
                  <button
                    key={`pm_${pm.id}`}
                    type="button"
                    class={`mailbox-avatar-btn mailbox-avatar-btn--personal ${isActive ? 'active' : ''}`}
                    title={pm.email}
                    onClick={e => handlePersonalMailboxClick(e, pm)}
                  >
                    <i class="bi bi-person-fill" style="font-size:0.8rem" />
                  </button>
                )
              })}
            </>
          )}
        </div>
      )}

      {/* Right: folder / tag panel */}
      <div class="email-sidebar-panel">

        {/* Compose button */}
        <button
          class="btn btn-primary w-100 mb-2 d-flex align-items-center gap-2"
          onClick={() => store.openCompose('new')}
        >
          <i class="bi bi-pencil-square" />
          <span>{i18n?.t('emails.compose')}</span>
        </button>

        {/* Active mailbox / inbox label */}
        {(mailboxes.length > 0 || visiblePersonalMailboxes.length > 0) && (
          <div class="px-1 mb-2" style="font-size:0.75rem;color:var(--bs-secondary-color);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
            {activeMailboxName}
          </div>
        )}

        {/* System folders */}
        <nav class="email-folders">
          {SYSTEM_FOLDERS.map(folder => {
            const count = getFolderCount(folder.key)
            const isActive = activeFolder === folder.key
            return (
              <a
                key={folder.key}
                href={`/emails/inbox?folder=${folder.key}`}
                class={`email-folder-item ${isActive ? 'active' : ''}`}
                data-email-drop-zone=""
                data-drop-folder-key={folder.key}
                onClick={e => handleFolderClick(e, folder.key)}
              >
                <i class={`bi ${folder.icon}`} />
                <span class="flex-grow-1">{i18n?.t(folder.labelKey)}</span>
                <UnreadBadge count={count} id={`sidebar_folder_${folder.key}_count`} />
              </a>
            )
          })}
        </nav>

        {/* Custom folders */}
        <hr class="my-2" />
        <div class="d-flex align-items-center justify-content-between px-2 mb-1">
          <span class="email-sidebar-section-title mb-0">{i18n?.t('emails.custom_folders')}</span>
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0"
            title={i18n?.t('emails.new_folder')}
            onClick={e => {
              const form = e.currentTarget.closest('.d-flex')?.nextElementSibling
              form?.classList.toggle('d-none')
            }}
          >
            <i class="bi bi-folder-plus" />
          </button>
        </div>

        {/* New folder form (hidden by default) */}
        <div class="px-2 mb-1 d-none" data-new-folder-form>
          <form onSubmit={handleNewFolderSubmit}>
            <div class="input-group input-group-sm">
              <input
                type="text"
                name="email_folder[name]"
                class="form-control form-control-sm"
                placeholder={i18n?.t('emails.new_folder')}
              />
              <button type="submit" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-check" />
              </button>
            </div>
          </form>
        </div>

        <nav class="email-folders" id="email_custom_folders">
          {emailFolders.map(folder => {
            const count = getCustomFolderCount(folder.id)
            return (
              <a
                key={folder.id}
                href={`/emails/inbox?email_folder_id=${folder.id}`}
                class={`email-folder-item ${activeFolder === `folder_${folder.id}` ? 'active' : ''}`}
                data-email-drop-zone=""
                data-drop-folder-id={folder.id}
                onClick={e => { e.preventDefault(); store.setActiveFolder(`folder_${folder.id}`) }}
              >
                <i class="bi bi-folder" style={folder.color ? { color: folder.color } : undefined} />
                <span class="flex-grow-1 text-truncate">{folder.name}</span>
                <UnreadBadge count={count} id={`sidebar_custom_folder_${folder.id}_count`} />
              </a>
            )
          })}
        </nav>

        {/* Tags */}
        {emailTags.length > 0 && (
          <>
            <hr class="my-2" />
            <div class="email-sidebar-section-title">{i18n?.t('emails.tags_label')}</div>
            <nav class="email-folders">
              {emailTags.map(tag => {
                const count = getTagCount(tag.id)
                return (
                  <a
                    key={tag.id}
                    href={`/emails/inbox?tag_ids[]=${tag.id}`}
                    class={`email-folder-item ${activeFolder === `tag_${tag.id}` ? 'active' : ''}`}
                    onClick={e => { e.preventDefault(); store.setActiveFolder(`tag_${tag.id}`) }}
                  >
                    <i class="bi bi-tag-fill" style={tag.color ? { color: tag.color } : undefined} />
                    <span class="flex-grow-1 text-truncate">{tag.tag}</span>
                    <UnreadBadge count={count} id={`sidebar_tag_${tag.id}_count`} />
                  </a>
                )
              })}
            </nav>
          </>
        )}

        {/* Settings section (collapsible) */}
        <hr class="my-2" />
        <button
          class="email-folder-item w-100 text-start border-0 bg-transparent"
          onClick={() => setSettingsOpen(o => !o)}
        >
          <i class="bi bi-gear" />
          <span class="flex-grow-1">{i18n?.t('emails.settings')}</span>
          <i class={`bi ${settingsOpen ? 'bi-chevron-up' : 'bi-chevron-down'} small`} />
        </button>
        {settingsOpen && (
          <div class="ps-3">
            <a href="/emails/statistics" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-bar-chart-line" />
              <span>{i18n?.t('emails.statistics.sidebar_link')}</span>
            </a>
            <a href="/emails/email_rules" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-funnel" />
              <span>{i18n?.t('emails.email_rules.sidebar_link')}</span>
            </a>
            <a href="/emails/message_trace" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-search" />
              <span>{i18n?.t('emails.message_trace.sidebar_link')}</span>
            </a>
            <a href="/mailboxes" class="email-folder-item" data-turbo-frame="_top">
              <i class="bi bi-mailbox" />
              <span>{i18n?.t('emails.mailbox_settings')}</span>
            </a>
          </div>
        )}

      </div>{/* end .email-sidebar-panel */}

    </div>
  )
}

function UnreadBadge({ count, id }) {
  if (!count || count <= 0) {
    return <span id={id} class="badge bg-secondary-subtle text-secondary rounded-pill small d-none" />
  }
  return (
    <span id={id} class="badge bg-secondary-subtle text-secondary rounded-pill small">
      {count}
    </span>
  )
}
